import loadCustomCategoryProps from "@hardware/Store/Handlers/Category/CustomCategoryProps";
import { CustomState } from "@hardware/Store/Reducers";
import transformContentHtmlCustom from "@hardware/Utilities/transformContentHtmlCustom";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    return {
        customCategoryProps: state.categoryProps,
    };
};

const mapDispatchToProps = {
    setView,
    loadCustomCategoryProps,
};

type Props = HasCategoryContext &
    WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CustomProductListHeaderStyles {
    wrapper?: InjectableCss;
    listViewClickable?: ClickablePresentationProps;
    listViewIcon?: IconPresentationProps;
    gridViewClickable?: ClickablePresentationProps;
    gridViewIcon?: IconPresentationProps;
    tableViewClickable?: ClickablePresentationProps;
    tableViewIcon?: IconPresentationProps;
    image?: LazyImageProps;
    heading?: TypographyPresentationProps;
    contentwrapper?: InjectableCss;
    contentwrapperNoImage?: InjectableCss;
}

export const customProductListHeaderStyles: CustomProductListHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            justify-content: flex-start;
            align-items: start;
            gap: 10px;
            margin: 10px 0;

            @media (max-width: 767px) {
                flex-direction: column;
            }
        `,
    },
    heading: {
        variant: "h2",
        css: css`
            margin: 0;
            padding: 0;
            line-height: 45px;
        `,
    },
    image: {
        css: css`
            max-width: 280px;
            max-height: 280px;
            width: 280px;
            height: 280px;

            @media (max-width: 767px) {
                height: 100%;
                img {
                    max-width: 100%;
                    margin-bottom: 15px;
                    max-height: unset;
                }
            }
        `,
    },
    contentwrapper: {
        css: css`
            /* Content-top images are parsed into <LazyImage as="span"> wrappers, which the global
               LazyImage override pins to 179px. Target those spans/imgs here (doubled "&&" class to
               outrank the global defaults) so the category diagrams render at double size. */
            && span {
                max-width: 358px;
                max-height: 358px;
                width: 358px;
                height: 358px;
            }

            && span img {
                max-width: 358px;
                max-height: 358px;
            }

            @media (max-width: 767px) {
                && span {
                    max-width: 100%;
                    width: 100%;
                    height: 100%;
                }

                && span img {
                    max-width: 100%;
                    margin-bottom: 15px;
                    max-height: unset;
                }
            }
        `,
    },
    contentwrapperNoImage: {
        css: css`
            margin-left: 300px;

            /* Same doubling as contentwrapper for the parsed content <LazyImage as="span"> diagrams. */
            && span {
                max-width: 358px;
                max-height: 358px;
                width: 358px;
                height: 358px;
            }

            && span img {
                max-width: 358px;
                max-height: 358px;
            }

            @media (max-width: 767px) {
                margin-left: 0;
                && span {
                    max-width: 100%;
                    width: 100%;
                    height: 100%;
                }

                && span img {
                    max-width: 100%;
                    margin-bottom: 15px;
                    max-height: unset;
                }
            }
        `,
    },
};

const styles = customProductListHeaderStyles;

const CustomProductListHeader = ({ category, loadCustomCategoryProps, customCategoryProps }: Props) => {
    const [isGridView, setIsGridView] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const [showContentTop, setContentTop] = useState("");
    const lastLoadedCategoryIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (category) {
            const Id = category.id.split(":")[0];

            // Only load if this is a different category than the last one loaded
            if (lastLoadedCategoryIdRef.current !== Id) {
                lastLoadedCategoryIdRef.current = Id;
                loadCustomCategoryProps({ categoryId: Id });
            }
        }
    }, [category, loadCustomCategoryProps]);

    useEffect(() => {
        if (customCategoryProps) {
            setIsGridView(customCategoryProps.gridview);
            setShowImage(customCategoryProps.categoryShowImageOnPLP);
            setContentTop(customCategoryProps.contentTop);
        }
    }, [customCategoryProps]);
    // verndale customization
    if (!isGridView) {
        return (
            <>
                {!showImage && !showContentTop && (
                    <Typography
                        data-test-selector={`categoryName_${category?.shortDescription ?? category?.name}`}
                        {...styles.heading}
                    >
                        {category?.shortDescription ?? category?.name}
                    </Typography>
                )}
                <StyledWrapper {...styles.wrapper}>
                    {category && showImage && (
                        <LazyImage
                            src={category.largeImagePath}
                            altText={category.imageAltText}
                            {...styles.image}
                            data-test-selector="categoryImagePath"
                            width="100px"
                            height="100px"
                        />
                    )}
                    <StyledWrapper>
                        {category && showContentTop && showImage && (
                            <StyledWrapper
                                {...styles.contentwrapper}
                                data-test-selector="categoryDetailBanner_htmlContent"
                            >
                                {parse(transformContentHtmlCustom(showContentTop), parserOptions)}
                            </StyledWrapper>
                        )}
                        {category && showContentTop && !showImage && (
                            <StyledWrapper
                                {...styles.contentwrapperNoImage}
                                data-test-selector="categoryDetailBanner_htmlContent"
                            >
                                {parse(transformContentHtmlCustom(showContentTop), parserOptions)}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </>
        );
    }

    return (
        <>
            {!showImage && !showContentTop && (
                <StyledWrapper>
                    <Typography
                        data-test-selector={`categoryName_${category?.shortDescription ?? category?.name}`}
                        {...styles.heading}
                    >
                        {category?.shortDescription ?? category?.name}
                    </Typography>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.wrapper}>
                {category && showImage && (
                    <LazyImage
                        src={category.largeImagePath}
                        altText={category.imageAltText}
                        {...styles.image}
                        data-test-selector="categoryImagePath"
                        width="100px"
                        height="100px"
                    />
                )}
                <StyledWrapper>
                    {category && showContentTop && (
                        <StyledWrapper {...styles.contentwrapper} data-test-selector="categoryDetailBanner_htmlContent">
                            {parse(transformContentHtmlCustom(showContentTop), parserOptions)}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(CustomProductListHeader as any) as any) as any,
    definition: {
        group: "Product List",
        displayName: "Product List Header",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
